import {useCallback, useEffect, useRef, useState} from 'react';

import {getMedicationCheckinsApi, markMedicationDoseApi} from 'redux/api/patientHome';
import {IMedicationCheckin, MedicationMarkStatus} from 'redux/intervention/interfaces';

export type MedicationLoadState = 'loading' | 'ready' | 'error';

const REFRESH_MS = 30000;

// Owns the patient's medication doses: fetching, background refresh (so doses the engine flips to
// missed show up), and optimistic mark taken / skip. Shared by the Medications page and, later,
// the dashboard summary, so the two can never disagree.
export function useMedicationCheckins() {
  const [doses, setDoses] = useState<IMedicationCheckin[]>([]);
  const [loadState, setLoadState] = useState<MedicationLoadState>('loading');
  const [markFailed, setMarkFailed] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());

  const dosesRef = useRef<IMedicationCheckin[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    dosesRef.current = doses;
  }, [doses]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const response = await getMedicationCheckinsApi();

      if (mountedRef.current) {
        setDoses(response.data.checkins ?? []);
        setLoadState('ready');
      }
    } catch {
      // A failed background refresh keeps the doses already on screen.
      if (mountedRef.current) {
        setLoadState((previous) => (previous === 'ready' ? 'ready' : 'error'));
      }
    }
  }, []);

  const retry = useCallback(() => {
    setLoadState('loading');
    load();
  }, [load]);

  useEffect(() => {
    load();

    const interval = window.setInterval(load, REFRESH_MS);
    const onFocus = () => load();

    window.addEventListener('focus', onFocus);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const mark = useCallback(async (logId: number, status: MedicationMarkStatus) => {
    const previous = dosesRef.current.find((dose) => dose.logId === logId);

    setMarkFailed(false);
    setBusyIds((ids) => new Set(ids).add(logId));
    setDoses((current) => current.map((dose) => (
      dose.logId === logId ? {...dose, status, markedAt: new Date().toISOString()} : dose
    )));

    try {
      await markMedicationDoseApi(logId, status);
    } catch {
      if (mountedRef.current) {
        setMarkFailed(true);
        if (previous) {
          setDoses((current) => current.map((dose) => (dose.logId === logId ? previous : dose)));
        }
      }
      // The dose may already have been resolved elsewhere (409), so show the true state.
      load();
    } finally {
      if (mountedRef.current) {
        setBusyIds((ids) => {
          const next = new Set(ids);

          next.delete(logId);

          return next;
        });
      }
    }
  }, [load]);

  return {doses, loadState, markFailed, busyIds, mark, retry};
}
